/**
 * ColumnPolicyEditor — the screen that makes "transcripts only if explicitly selected" real.
 *
 * CONTRACT §6 defines three tiers and this editor is the only place a human moves a column
 * between them:
 *
 *   default   ships unless a schedule turns it off
 *   opt_in    ships ONLY when the schedule lists it in `include_opt_in` — off until then
 *   never     unreachable by any selection, at any time, by anyone
 *
 * Three behaviours here are load-bearing, and each one exists because of a way this goes
 * wrong in production:
 *
 * 1. **Opt-in defaults OFF.** A column in the opt-in tier ships on no schedule until someone
 *    lists it. The editor states that on every opt-in row rather than leaving it implied,
 *    because "it is configured on the client" reads like "it is being sent".
 *
 * 2. **Moving call content into `default` is the dangerous move, and it is the one that looks
 *    harmless.** Source is the test, not length (§6): a 55-character AI summary of a
 *    borrower's bereavement is the same class of data as a 2,443-character transcript, and a
 *    recording URL carries the entire conversation in 90 characters. Promoting any of those to
 *    the default tier makes them ship in every scheduled report, to every recipient, with
 *    nobody selecting anything — so it goes through a typed confirmation naming the column.
 *
 * 3. **Conduct-QA columns are locked, not discouraged.** `critical_breaches` and its family are
 *    our own agent's defect record (§6.3) — offer_never_made, ptp_fabricated,
 *    Pitched_After_Bereavement. The API refuses to store them in default/opt_in, the emit path
 *    raises on them, and here they render with a lock and no reachable control. A disabled
 *    button that explains itself is worth more than a validation error after a save.
 *
 * The classifier below mirrors `packages/core/nrd/policy.py` exactly (ALWAYS_NEVER,
 * ALWAYS_NEVER_PATTERNS, normalise_header, and `never` > `opt_in` > `default` precedence). If
 * the two ever disagree the Python one wins — it is what actually gates the file.
 */

import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import Badge, { PolicyBadge } from '@/components/Badge';
import Button, { ButtonGroup } from '@/components/Button';
import ConfirmDialog from '@/components/ConfirmDialog';
import EmptyState from '@/components/EmptyState';
import { Checkbox, Input } from '@/components/Field';
import Table, { type Column } from '@/components/Table';
import { formatCount, pluralise } from '@/lib/format';
import type { ColumnPolicy } from '@/lib/types';

// ---------------------------------------------------------------------------
// Classification — mirrors packages/core/nrd/policy.py
// ---------------------------------------------------------------------------

export type ColumnTier = 'default' | 'opt_in' | 'never';
/** A column in no list, while `default` is an open tier: it ships with everything else. */
export type ColumnTierOrUnlisted = ColumnTier | 'unlisted';

/**
 * Header identity, case/space/punctuation insensitive.
 * Mirrors `nrd.fieldmap.normalise_header`: `"Phone Number"` → `phone_number`.
 */
export function normaliseColumn(name: string): string {
  return String(name ?? '')
    .replace(/[\uFEFF\u200B\u200C\u200D\u2060]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[\s\-./]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * Conduct-QA columns. Our own agent's defect record, never a client deliverable.
 * Mirrors `nrd.policy.ALWAYS_NEVER`.
 */
export const ALWAYS_NEVER_COLUMNS: readonly string[] = [
  'critical_breaches',
  'critical_breach',
  'quality_flags',
  'quality_flag',
  'audio_quality_failure',
  'conduct_flags',
  'conduct_qa',
  'agent_qa',
  'agent_defects',
  'compliance_breaches',
];

const ALWAYS_NEVER_SET = new Set(ALWAYS_NEVER_COLUMNS);

/** Mirrors `nrd.policy.ALWAYS_NEVER_PATTERNS`. */
const ALWAYS_NEVER_PATTERNS: readonly RegExp[] = [
  /critical_breach/,
  /conduct_?qa/,
  /quality_?flags?$/,
  /^agent_(qa|defect|violation)/,
  /(^|_)breaches$/,
];

/** True for a column no configuration can ever ship. The control is locked, not just warned. */
export function isAlwaysNever(column: string): boolean {
  const norm = normaliseColumn(column);
  if (!norm) return false;
  if (ALWAYS_NEVER_SET.has(norm)) return true;
  return ALWAYS_NEVER_PATTERNS.some((pattern) => pattern.test(norm));
}

/** Which tier a column falls in for this policy. `never` beats `opt_in` beats `default`. */
export function classifyColumn(policy: ColumnPolicy, column: string): ColumnTierOrUnlisted {
  const norm = normaliseColumn(column);
  if (isAlwaysNever(column)) return 'never';
  if ((policy.never ?? []).some((c) => normaliseColumn(c) === norm)) return 'never';
  if ((policy.opt_in ?? []).some((c) => normaliseColumn(c) === norm)) return 'opt_in';
  if ((policy.default ?? []).length > 0) {
    return (policy.default ?? []).some((c) => normaliseColumn(c) === norm) ? 'default' : 'unlisted';
  }
  // Empty default list = open default tier: anything unlisted ships.
  return 'default';
}

/** True when the client enumerates its default tier, i.e. the default list is a closed allow-list. */
export function hasExplicitDefault(policy: ColumnPolicy): boolean {
  return (policy.default ?? []).length > 0;
}

// ---------------------------------------------------------------------------
// Call content — what makes a column opt-in in the first place
// ---------------------------------------------------------------------------

/**
 * `speech` carries what the borrower actually said (or a machine's rendering of it);
 * `recording` is the whole conversation behind a URL; `derived` is a judgement our agent
 * made from the call. CONTRACT §6: all three are opt-in, because source is the test, not
 * length — the shortest of them is still call content.
 */
export type ContentKind = 'speech' | 'recording' | 'derived';

interface ContentRule {
  kind: ContentKind;
  test: RegExp;
  why: string;
}

const CONTENT_RULES: readonly ContentRule[] = [
  {
    kind: 'recording',
    test: /(^|_)(recording|recordings|audio|call_url|call_link)(_|$)/,
    why: 'a link to the call audio — the entire conversation, however short the URL is',
  },
  {
    kind: 'speech',
    test: /(verbatim|transcript|utterance|evidence|summary|quote|remark|dialogue|conversation|_said|signals?$|(^|_)notes?$|_detail$|_stated$)/,
    why: "what the borrower said, or a machine's rendering of it",
  },
  {
    kind: 'derived',
    // Deliberately token-anchored rather than broad: `category` on its own would flag
    // `Disposition_category`, which is a grouping of the outcome and not call content, and a
    // warning that cries wolf on a default column is a warning nobody reads.
    test: /(assessment|income|occupation|intent|override|primary_queue|ptp_quality|recovery_segment|counter_offer|highest_amount|undercut|recontact|willing|(^|_)reason(_|$)|(^|_)ability(_|$)|(^|_)earners?$)/,
    why: 'a judgement our agent formed from what was said on the call',
  },
];

/** What kind of call content a column carries, or null if the name shows no sign of any. */
export function callContent(column: string): { kind: ContentKind; why: string } | null {
  const norm = normaliseColumn(column);
  if (!norm) return null;
  for (const rule of CONTENT_RULES) {
    if (rule.test.test(norm)) return { kind: rule.kind, why: rule.why };
  }
  return null;
}

const CONTENT_LABEL: Record<ContentKind, string> = {
  speech: 'borrower speech',
  recording: 'call audio',
  derived: 'derived from the call',
};

/**
 * What a column carries. A CLASSIFICATION: it cannot be wrong and it does not change on
 * its own, so it is a mark rather than a pill. Whether that is a problem depends on the
 * TIER, and the tier has its own badge and its own warning beside this one.
 */
function ContentBadge({ kind }: { kind: ContentKind }) {
  return (
    <Badge
      variant="plain"
      glyph={kind === 'recording' ? '♪' : '“'}
      title={
        kind === 'derived'
          ? 'Derived from the call. Opt-in under CONTRACT §6 — source is the test, not length.'
          : 'Carries what was said on the call. Opt-in under CONTRACT §6.'
      }
    >
      {CONTENT_LABEL[kind]}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Policy mutation
// ---------------------------------------------------------------------------

function withoutColumn(list: string[] | undefined, column: string): string[] {
  const norm = normaliseColumn(column);
  return (list ?? []).filter((c) => normaliseColumn(c) !== norm);
}

/**
 * Move a column to a tier, removing it from the other two first.
 *
 * The API rejects a column that appears in two tiers (validate.validate_column_policy), so
 * the editor never produces one: a tier change is a move, never an add.
 */
export function setColumnTier(
  policy: ColumnPolicy,
  column: string,
  tier: ColumnTierOrUnlisted,
): ColumnPolicy {
  const next: ColumnPolicy = {
    default: withoutColumn(policy.default, column),
    opt_in: withoutColumn(policy.opt_in, column),
    never: withoutColumn(policy.never, column),
  };
  if (tier === 'unlisted') return next;
  // isAlwaysNever columns are stored in `never` for the record even though the classifier
  // would land them there anyway — an explicit list is what an auditor reads.
  next[tier] = [...(next[tier] ?? []), column];
  return next;
}

/** Reorder within the default tier. Its order is the column order of the delivered file. */
function moveWithinDefault(policy: ColumnPolicy, column: string, delta: -1 | 1): ColumnPolicy {
  const list = [...(policy.default ?? [])];
  const norm = normaliseColumn(column);
  const index = list.findIndex((c) => normaliseColumn(c) === norm);
  if (index < 0) return policy;
  const target = index + delta;
  if (target < 0 || target >= list.length) return policy;
  const moving = list[index];
  const displaced = list[target];
  if (moving === undefined || displaced === undefined) return policy;
  list[index] = displaced;
  list[target] = moving;
  return { ...policy, default: list };
}

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

interface PolicyRow {
  /** The column name exactly as configured — never normalised for display. */
  column: string;
  tier: ColumnTierOrUnlisted;
  locked: boolean;
  content: { kind: ContentKind; why: string } | null;
  /** Position within the default list, used for reordering. -1 when not in it. */
  defaultIndex: number;
}

const TIER_ORDER: Record<ColumnTierOrUnlisted, number> = {
  default: 0,
  opt_in: 1,
  never: 2,
  unlisted: 3,
};

const TIER_HEADING: Record<ColumnTierOrUnlisted, string> = {
  default: 'Default',
  opt_in: 'Opt-in',
  never: 'Never',
  unlisted: 'Unlisted',
};

/**
 * A row in the table: either a column, or the heading that opens its tier block.
 *
 * The per-tier sentences used to be rendered per ROW, in the widest column of the table —
 * 40 copies of four sentences, carrying no per-row information at all. They are stated
 * once per block here, verbatim, and the rows get their width back.
 */
type TableRow = { kind: 'group'; tier: ColumnTierOrUnlisted; count: number } | ({ kind: 'row' } & PolicyRow);

function withGroupHeadings(rows: PolicyRow[]): TableRow[] {
  const out: TableRow[] = [];
  let open: ColumnTierOrUnlisted | null = null;
  for (const row of rows) {
    if (row.tier !== open) {
      open = row.tier;
      out.push({
        kind: 'group',
        tier: row.tier,
        count: rows.filter((r) => r.tier === row.tier).length,
      });
    }
    out.push({ kind: 'row', ...row });
  }
  return out;
}

function buildRows(policy: ColumnPolicy, knownColumns: readonly string[]): PolicyRow[] {
  const seen = new Map<string, string>();
  const push = (column: string): void => {
    const norm = normaliseColumn(column);
    if (!norm || seen.has(norm)) return;
    seen.set(norm, column);
  };

  (policy.default ?? []).forEach(push);
  (policy.opt_in ?? []).forEach(push);
  (policy.never ?? []).forEach(push);
  knownColumns.forEach(push);

  const defaults = (policy.default ?? []).map(normaliseColumn);

  return Array.from(seen.values())
    .map<PolicyRow>((column) => ({
      column,
      tier: classifyColumn(policy, column),
      locked: isAlwaysNever(column),
      content: callContent(column),
      defaultIndex: defaults.indexOf(normaliseColumn(column)),
    }))
    .sort((a, b) => {
      const byTier = TIER_ORDER[a.tier] - TIER_ORDER[b.tier];
      if (byTier !== 0) return byTier;
      if (a.tier === 'default' && a.defaultIndex >= 0 && b.defaultIndex >= 0) {
        return a.defaultIndex - b.defaultIndex;
      }
      return normaliseColumn(a.column).localeCompare(normaliseColumn(b.column));
    });
}

// ---------------------------------------------------------------------------
// Editor
// ---------------------------------------------------------------------------

export interface ColumnPolicyEditorProps {
  value: ColumnPolicy;
  onChange: (next: ColumnPolicy) => void;
  /**
   * Column names known from elsewhere — field_map values, a recent run's emitted list.
   * They render as rows so an operator can classify a column without typing it.
   */
  knownColumns?: string[];
  /** Named in the warnings, so the operator sees whose borrower data this is. */
  companyName?: string;
  disabled?: boolean;
}

interface PendingChange {
  column: string;
  tier: ColumnTierOrUnlisted;
  title: string;
  consequence: ReactNode;
  body: ReactNode;
  tone: 'danger' | 'warn';
  requirePhrase: boolean;
}

export function ColumnPolicyEditor({
  value,
  onChange,
  knownColumns = [],
  companyName,
  disabled = false,
}: ColumnPolicyEditorProps) {
  const [filter, setFilter] = useState('');
  const [draftColumn, setDraftColumn] = useState('');
  const [pending, setPending] = useState<PendingChange | null>(null);

  const rows = useMemo(() => buildRows(value, knownColumns), [value, knownColumns]);
  const explicitDefault = hasExplicitDefault(value);

  const counts = useMemo(() => {
    const out = { default: 0, opt_in: 0, never: 0, content: 0 };
    for (const row of rows) {
      if (row.tier !== 'unlisted') out[row.tier] += 1;
      if (row.content && row.tier === 'default') out.content += 1;
    }
    return out;
  }, [rows]);

  const filtered = useMemo(() => {
    const needle = normaliseColumn(filter);
    if (!needle) return rows;
    return rows.filter((row) => normaliseColumn(row.column).includes(needle));
  }, [rows, filter]);

  const lender = companyName ? `${companyName} borrowers` : 'the borrowers on this account';

  const apply = (column: string, tier: ColumnTierOrUnlisted): void => {
    onChange(setColumnTier(value, column, tier));
  };

  /** Everything that needs a human to stop and read goes through here. */
  const requestTier = (row: PolicyRow, tier: ColumnTierOrUnlisted): void => {
    if (disabled || row.tier === tier) return;

    if (row.locked && tier !== 'never') {
      // Unreachable from the UI (the controls are disabled) — belt and braces.
      return;
    }

    const promotingToDefault = tier === 'default';
    const openingUp = tier === 'opt_in' && row.tier === 'never';

    if (promotingToDefault && row.content) {
      setPending({
        column: row.column,
        tier,
        tone: 'danger',
        requirePhrase: true,
        title: 'Ship this on every report?',
        body: (
          <div className="stack">
            <p>
              <code>{row.column}</code> carries {row.content.why}.
            </p>
            <p>
              In the default tier it ships in <strong>every scheduled report</strong> for this
              client, to <strong>every recipient</strong> on the list, with nobody selecting it.
              The opt-in tier is where this belongs: there it ships only when a schedule names it.
            </p>
            {!explicitDefault ? (
              <p className="small dim">
                This also closes the default tier — see the note below about the allow-list.
              </p>
            ) : null}
          </div>
        ),
        consequence: `Content about ${lender} leaves the building on every future run of every schedule for this client, unselected and unreviewed.`,
      });
      return;
    }

    if (promotingToDefault && !explicitDefault) {
      setPending({
        column: row.column,
        tier,
        tone: 'warn',
        requirePhrase: false,
        title: 'Close the default tier?',
        body: (
          <div className="stack">
            <p>
              This client's default list is empty, which means the default tier is <em>open</em>:
              every column in the source that is not opt-in and not never ships today.
            </p>
            <p>
              Listing <code>{row.column}</code> makes the default list a closed allow-list. From
              the next run, <strong>only columns you have listed here ship</strong> — every other
              source column silently stops appearing in the client's file.
            </p>
          </div>
        ),
        consequence:
          'Columns the client is receiving today will disappear from their next report unless you list them too.',
      });
      return;
    }

    if (openingUp && row.content) {
      setPending({
        column: row.column,
        tier,
        tone: 'warn',
        requirePhrase: false,
        title: 'Make this selectable?',
        body: (
          <p>
            <code>{row.column}</code> carries {row.content.why}. Moving it out of never puts it
            within reach of a schedule: it still ships only when one lists it, but it stops being
            unreachable.
          </p>
        ),
        consequence: 'Any operator editing a schedule can add it to a delivered report from now on.',
      });
      return;
    }

    if (tier === 'unlisted') {
      setPending({
        column: row.column,
        tier,
        tone: 'warn',
        requirePhrase: false,
        title: 'Remove from the policy?',
        body: (
          <p>
            <code>{row.column}</code> stops being classified.{' '}
            {explicitDefault
              ? 'With a closed default list, an unclassified column does not ship at all.'
              : 'With an open default tier, an unclassified column ships with everything else — which for call content is exactly the wrong outcome.'}
          </p>
        ),
        consequence: explicitDefault
          ? 'The column stops appearing in delivered files.'
          : 'The column starts shipping by default on the next run.',
      });
      return;
    }

    apply(row.column, tier);
  };

  const addColumn = (): void => {
    const column = draftColumn.trim();
    if (!column) return;
    const existing = rows.find((row) => normaliseColumn(row.column) === normaliseColumn(column));
    if (existing) {
      setFilter(column);
      setDraftColumn('');
      return;
    }
    // Conduct QA is locked on sight; everything else lands in opt-in. Never in default: a
    // column nobody has classified must not start shipping because it was typed into a box.
    const tier: ColumnTier = isAlwaysNever(column) ? 'never' : 'opt_in';
    onChange(setColumnTier(value, column, tier));
    setDraftColumn('');
  };

  /** The standing sentence for a tier — stated once per block, never per row. */
  const tierSentence = (tier: ColumnTierOrUnlisted): ReactNode => {
    if (tier === 'never') {
      return <span className="small">Never leaves the building, whatever a schedule asks for.</span>;
    }
    if (tier === 'opt_in') {
      return (
        <span className="small">
          <strong>Off</strong> on every schedule until one lists it in <code>include_opt_in</code>.
          Suppressed on rows where the borrower was not the counterparty.
        </span>
      );
    }
    if (tier === 'unlisted') {
      return (
        <span className="small">
          {explicitDefault
            ? 'Not in the allow-list, so it does not ship.'
            : 'Unclassified — ships with everything else while the default tier is open.'}
        </span>
      );
    }
    // With an open tier a column ships because nothing classified it, which is not the
    // same statement as "someone listed it" — and only one of those survives a new column
    // appearing in the source next week.
    return explicitDefault ? (
      <span className="small">Ships on every report, in the order shown.</span>
    ) : (
      <span className="small">
        Ships on every report because the default tier is open — these columns are not
        individually listed.
      </span>
    );
  };

  const columns: Column<TableRow>[] = [
    {
      // The width-less column: the column name, its marks, and — on a heading row — the
      // one statement that used to be repeated on every row beneath it.
      key: 'column',
      header: 'Source column',
      wrap: true,
      render: (row) => {
        if (row.kind === 'group') {
          return (
            <span className="cluster-tight" style={{ minWidth: 0 }}>
              <span className="eyebrow">
                {TIER_HEADING[row.tier]} · {formatCount(row.count)}
              </span>
              {tierSentence(row.tier)}
            </span>
          );
        }
        return (
          <span className="cluster-tight" style={{ minWidth: 0 }}>
            <span className="mono strong">{row.column}</span>
            {row.locked ? (
              <Badge tone="danger" glyph="⊠" terminalBad title="Conduct QA — locked by CONTRACT §6.3">
                locked
              </Badge>
            ) : null}
            {row.content ? <ContentBadge kind={row.content.kind} /> : null}
            {/* "We must never ship this" and "we are shipping this and should not be" are
                not one thing, so they no longer render as one mark. */}
            {!row.locked && row.tier === 'default' && row.content ? (
              <Badge
                tone="warn"
                glyph="▲"
                title={`Call content in the default tier: ${row.content.why}. It ships on every run to every recipient with nobody selecting it.`}
              >
                shipping by default
              </Badge>
            ) : null}
          </span>
        );
      },
    },
    {
      // The tier, as hue + glyph + border, so it survives greyscale. It used to be
      // readable only from which button in the group was painted teal — which spent the
      // one accent the design reserves for "you can act on this" on state instead.
      key: 'tier',
      header: 'Tier',
      width: '116px',
      nowrap: true,
      render: (row) =>
        row.kind === 'group' ? null : row.tier === 'unlisted' ? (
          <Badge tone="muted" glyph="○" title="In no list on this client">
            unlisted
          </Badge>
        ) : (
          <PolicyBadge tier={row.tier} />
        ),
    },
    {
      key: 'moves',
      header: 'Move to',
      width: '182px',
      nowrap: true,
      headerTitle: 'The tiers this column can move to from where it is now.',
      render: (row) => {
        if (row.kind === 'group') return null;
        const moves = (['default', 'opt_in', 'never'] as const).filter(
          (tier) => tier !== row.tier && !(row.locked && tier !== 'never'),
        );
        if (moves.length === 0) {
          return (
            <span
              className="xs dim"
              title="Conduct-QA columns cannot be shipped by any configuration (CONTRACT §6.3)."
            >
              locked
            </span>
          );
        }
        return (
          // Graphite, because these are the moves you can make. The current tier is the
          // badge to the left, not a teal button in this group.
          <ButtonGroup label={`Move ${row.column} to`}>
            {moves.map((tier) => (
              <Button
                key={tier}
                size="sm"
                disabled={disabled}
                title={
                  tier === 'opt_in'
                    ? 'Ships only when a schedule lists it — off until then'
                    : tier === 'default'
                      ? 'Ships on every report unless a schedule turns it off'
                      : 'Unreachable by any selection'
                }
                onClick={() => requestTier(row, tier)}
              >
                {tier === 'opt_in' ? 'Opt-in' : tier === 'default' ? 'Default' : 'Never'}
              </Button>
            ))}
          </ButtonGroup>
        );
      },
    },
    {
      // The one genuinely per-row variable the old prose column never carried.
      key: 'position',
      header: 'In file',
      width: '92px',
      align: 'right',
      mono: true,
      headerTitle: 'Position in the delivered file. Default tier only, and only when the tier is closed.',
      render: (row) =>
        row.kind === 'row' && row.tier === 'default' && explicitDefault && row.defaultIndex >= 0 ? (
          formatCount(row.defaultIndex + 1)
        ) : (
          <span className="dim">—</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      width: '124px',
      align: 'right',
      nowrap: true,
      render: (row) =>
        row.kind === 'group' ? null : (
          <div className="cluster-tight" style={{ justifyContent: 'flex-end' }}>
            {row.tier === 'default' && explicitDefault && !row.locked ? (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  iconOnly
                  icon={<span aria-hidden="true">↑</span>}
                  disabled={disabled || row.defaultIndex <= 0}
                  title={`Move ${row.column} earlier in the file`}
                  aria-label={`Move ${row.column} earlier in the file`}
                  onClick={() => onChange(moveWithinDefault(value, row.column, -1))}
                />
                <Button
                  size="sm"
                  variant="ghost"
                  iconOnly
                  icon={<span aria-hidden="true">↓</span>}
                  disabled={disabled || row.defaultIndex >= (value.default ?? []).length - 1}
                  title={`Move ${row.column} later in the file`}
                  aria-label={`Move ${row.column} later in the file`}
                  onClick={() => onChange(moveWithinDefault(value, row.column, 1))}
                />
              </>
            ) : null}
            {row.tier !== 'unlisted' && !row.locked ? (
              <Button
                size="sm"
                variant="ghost"
                iconOnly
                icon={<span aria-hidden="true">✕</span>}
                disabled={disabled}
                title={`Remove ${row.column} from the policy`}
                aria-label={`Remove ${row.column} from the policy`}
                onClick={() => requestTier(row, 'unlisted')}
              />
            ) : null}
          </div>
        ),
    },
  ];

  const tableRows = useMemo(() => withGroupHeadings(filtered), [filtered]);

  return (
    <div className="stack">
      {/* A closed tier is a value read back to the operator, not an alert, so it gets no
          box. An OPEN tier is a live state someone can act on — that one keeps the
          callout. Both sentences are unchanged. */}
      {explicitDefault ? (
        <div className="readback">
          <span className="strong">Closed default tier.</span> Only the{' '}
          {pluralise(counts.default, 'column')} listed as Default ship, in the order shown. A new
          column appearing in the source will not reach the client until someone lists it here.
        </div>
      ) : (
        <div className="callout tone-warn">
          <span className="callout-glyph" aria-hidden="true">
            ▲
          </span>
          <span>
            <strong>Open default tier.</strong> The default list is empty, so every column in the
            source that is not opt-in and not never ships today — including a column that appears
            in the export for the first time tomorrow. Classify call content as{' '}
            <strong>opt-in</strong> to keep it off, or list default columns explicitly to close
            the tier.
          </span>
        </div>
      )}

      {counts.content > 0 ? (
        <div className="callout tone-danger">
          <span className="callout-glyph" aria-hidden="true">
            ✕
          </span>
          <span>
            {pluralise(counts.content, 'default-tier column')} on this client{' '}
            {counts.content === 1 ? 'carries' : 'carry'} call content. That ships to every recipient
            on every run with nobody selecting it. Move {counts.content === 1 ? 'it' : 'them'} to
            opt-in unless the client has signed for it.
          </span>
        </div>
      ) : null}

      {/* One toolbar, doing both jobs. "Add a column" used to sit BELOW a table that can
          run 1,500px, so on a long policy it was off-screen from the filter that told you
          the column was missing. */}
      <div className="toolbar">
        <Input
          size="sm"
          value={filter}
          placeholder="Filter columns…"
          aria-label="Filter columns"
          onChange={(event) => setFilter(event.target.value)}
          style={{ maxWidth: 200 }}
        />
        {filter ? (
          <Button size="sm" variant="ghost" onClick={() => setFilter('')}>
            Clear
          </Button>
        ) : null}

        <span className="dim" aria-hidden="true">
          ·
        </span>

        <label className="xs dim" htmlFor="nrd-policy-add">
          Add a column
        </label>
        <Input
          id="nrd-policy-add"
          size="sm"
          mono
          value={draftColumn}
          disabled={disabled}
          placeholder="exact source column name"
          spellCheck={false}
          autoComplete="off"
          onChange={(event) => setDraftColumn(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addColumn();
            }
          }}
          style={{ maxWidth: 240 }}
        />
        <Button size="sm" disabled={disabled || !draftColumn.trim()} onClick={addColumn}>
          Add as opt-in
        </Button>

        <div className="grow" />

        <span className="cluster-tight xs dim">
          <PolicyBadge tier="default" /> {counts.default}
          <PolicyBadge tier="opt_in" /> {counts.opt_in}
          <PolicyBadge tier="never" /> {counts.never}
        </span>
      </div>

      <Table<TableRow>
        columns={columns}
        rows={tableRows}
        rowKey={(row) =>
          row.kind === 'group' ? `group-${row.tier}` : `col-${normaliseColumn(row.column)}`
        }
        dense
        // Two danger cases, told apart. `locked` is "we must never ship this"; call
        // content sitting in the default tier is "we are shipping this and should not
        // be" — a live misconfiguration, which is the one an operator can fix. The
        // opt-in tier is a legal configuration and no longer paints a warn rule on every
        // one of its rows.
        rowTone={(row) => {
          if (row.kind === 'group') return 'default';
          if (row.locked) return 'danger';
          if (row.tier === 'default' && row.content !== null) return 'warn';
          if (row.tier === 'never') return 'muted';
          return 'default';
        }}
        caption="Column policy — CONTRACT §6"
        empty={
          filter ? (
            <EmptyState
              tone="filtered"
              title="No column matches that"
              description="The filter is case- and punctuation-insensitive, so `Phone Number` and phone_number are the same search."
              action={
                <Button variant="primary" onClick={() => setFilter('')}>
                  Clear filter
                </Button>
              }
            />
          ) : (
            <EmptyState
              title="No columns classified"
              description="Nothing is opt-in and nothing is blocked, so every column in the source ships. Add the call-content columns below before the next dispatch."
            />
          )
        }
      />

      <span className="xs dim">
        A column added above lands in opt-in, never in default. Spell it exactly as the source
        header does.
      </span>

      <ConfirmDialog
        open={pending !== null}
        tone={pending?.tone ?? 'warn'}
        title={pending?.title ?? ''}
        consequence={pending?.consequence}
        confirmLabel={
          pending?.tier === 'default'
            ? 'Ship by default'
            : pending?.tier === 'unlisted'
              ? 'Remove'
              : 'Move'
        }
        confirmPhrase={pending?.requirePhrase ? pending.column : undefined}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          if (pending) apply(pending.column, pending.tier);
          setPending(null);
        }}
      >
        {pending?.body}
      </ConfirmDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Opt-in selection — the schedule side of the same policy
// ---------------------------------------------------------------------------

export interface OptInSelectorProps {
  /** The client's policy; only its opt_in tier is selectable. */
  policy: ColumnPolicy;
  /** Currently selected columns (a schedule's `include_opt_in`). Empty = nothing extra ships. */
  selected: string[];
  onChange: (next: string[]) => void;
  companyName?: string;
  disabled?: boolean;
}

/**
 * The control a schedule uses to turn opt-in columns on. Every box starts unchecked, and
 * checking one that carries borrower speech or call audio takes an explicit acknowledgement —
 * the same gate as promoting a column to the default tier, at the other end of the pipeline.
 */
export function OptInSelector({
  policy,
  selected,
  onChange,
  companyName,
  disabled = false,
}: OptInSelectorProps) {
  const [pending, setPending] = useState<string | null>(null);

  const available = useMemo(
    () => (policy.opt_in ?? []).filter((column) => !isAlwaysNever(column)),
    [policy.opt_in],
  );
  const selectedSet = useMemo(
    () => new Set(selected.map((c) => normaliseColumn(c))),
    [selected],
  );

  const toggle = (column: string, on: boolean): void => {
    if (on) {
      onChange([...selected.filter((c) => normaliseColumn(c) !== normaliseColumn(column)), column]);
    } else {
      onChange(selected.filter((c) => normaliseColumn(c) !== normaliseColumn(column)));
    }
  };

  const request = (column: string, on: boolean): void => {
    if (!on) {
      toggle(column, false);
      return;
    }
    const content = callContent(column);
    if (content && content.kind !== 'derived') {
      setPending(column);
      return;
    }
    toggle(column, true);
  };

  if (available.length === 0) {
    return (
      <p className="muted small">
        This client has no opt-in columns configured, so a schedule has nothing extra to select.
        Everything it ships comes from the default tier.
      </p>
    );
  }

  const pendingContent = pending ? callContent(pending) : null;

  return (
    <div className="stack">
      <div className="callout tone-info">
        <span className="callout-glyph" aria-hidden="true">
          i
        </span>
        <span>
          Every box here starts <strong>off</strong>. An opt-in column ships only on the schedules
          that list it, and even then it is blanked on rows where the borrower was not the
          counterparty (CONTRACT §6.1).
        </span>
      </div>

      <div className="stack-tight" role="group" aria-label="Opt-in columns">
        {available.map((column) => {
          const content = callContent(column);
          const on = selectedSet.has(normaliseColumn(column));
          return (
            <Checkbox
              key={normaliseColumn(column)}
              checked={on}
              disabled={disabled}
              onChange={(event) => request(column, event.target.checked)}
              label={
                <span className="cluster-tight">
                  <span className="mono">{column}</span>
                  {content ? <ContentBadge kind={content.kind} /> : null}
                </span>
              }
              hint={content ? content.why : 'client extra — no call content in the name'}
            />
          );
        })}
      </div>

      <div className="cluster">
        <Button
          size="sm"
          variant="ghost"
          disabled={disabled || selected.length === 0}
          onClick={() => onChange([])}
        >
          Clear all
        </Button>
        <span className="xs dim">
          {selected.length === 0
            ? 'Nothing selected — the report carries default-tier columns only.'
            : `${pluralise(selected.length, 'opt-in column')} will be added to this report.`}
        </span>
      </div>

      <ConfirmDialog
        open={pending !== null}
        tone="danger"
        title="Add call content to this report?"
        confirmLabel="Add the column"
        confirmPhrase={pending ?? undefined}
        consequence={`Every recipient of this report can read${
          pendingContent?.kind === 'recording' ? ' and replay' : ''
        } it${companyName ? ` for ${companyName} borrowers` : ''}, for the whole life of the link.`}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          if (pending) toggle(pending, true);
          setPending(null);
        }}
      >
        <p>
          <code>{pending}</code> carries {pendingContent?.why}. It is opt-in for that reason and it
          is off by default. Turn it on only when the client has asked for it in writing.
        </p>
      </ConfirmDialog>
    </div>
  );
}

export default ColumnPolicyEditor;
